/**
 * Tipos de producto — CRUD simple (slug + name).
 */
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Card, Input, Label, Button, PageHeader } from '@/components/ui'
import { tenantApi } from '@/lib/api'
import { useAuth } from '@/lib/auth-store'
import { Plus, Layers, Edit, Trash2, X } from 'lucide-react'

export const Route = createFileRoute('/store/$id/product-types')({
  component: ProductTypesPage,
})

interface ProductType {
  id: string
  slug: string
  name: string | null
  productCount: number
  createdAt: string
}

function ProductTypesPage() {
  const { activeTenantId } = useAuth()
  const [dialog, setDialog] = useState<{ mode: 'create' | 'edit'; type?: ProductType } | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['product-types'],
    queryFn: () => tenantApi.get<{ data: ProductType[] }>('/api/v1/product-types'),
    enabled: !!activeTenantId,
  })

  const list = data?.data ?? []

  return (
    <>
      <PageHeader
        title="Tipos de producto"
        description="Clasificación: camisetas, hoodies, pantalones, etc."
        action={
          <Button size="sm" onClick={() => setDialog({ mode: 'create' })}>
            <Plus className="size-4 mr-2" /> Nuevo tipo
          </Button>
        }
      />

      <div className="p-8 space-y-4">
        {isLoading && <p className="text-sm text-neutral-500">Cargando...</p>}
        {!isLoading && list.length === 0 && (
          <Card className="p-12 flex flex-col items-center justify-center text-center">
            <Layers className="size-12 text-neutral-700 mb-3" />
            <p className="text-base font-medium">Sin tipos definidos</p>
            <p className="text-sm text-neutral-500 mt-1">Agrupa productos con atributos similares (talla, color).</p>
          </Card>
        )}
        {list.length > 0 && (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-950 border-b border-neutral-800 text-xs text-neutral-500 uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-3">Nombre</th>
                    <th className="text-left px-4 py-3">Slug</th>
                    <th className="text-right px-4 py-3">Productos</th>
                    <th className="text-right px-4 py-3">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-800">
                  {list.map((t) => <TypeRow key={t.id} type={t} onEdit={() => setDialog({ mode: 'edit', type: t })} />)}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {dialog && <TypeDialog mode={dialog.mode} type={dialog.type} onClose={() => setDialog(null)} />}
    </>
  )
}

function TypeRow({ type, onEdit }: { type: ProductType; onEdit: () => void }) {
  const qc = useQueryClient()
  const remove = useMutation({
    mutationFn: () => tenantApi.del(`/api/v1/product-types/${type.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['product-types'] }),
  })
  return (
    <tr className="hover:bg-neutral-900/50">
      <td className="px-4 py-3 font-medium">{type.name ?? type.slug}</td>
      <td className="px-4 py-3 font-mono text-xs text-neutral-400">/{type.slug}</td>
      <td className="px-4 py-3 text-right tabular-nums">{type.productCount}</td>
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-1">
          <button onClick={onEdit} className="p-1.5 rounded text-neutral-400 hover:text-white hover:bg-neutral-800" title="Editar"><Edit className="size-4" /></button>
          <button onClick={() => { if (confirm('¿Eliminar este tipo?')) remove.mutate() }} className="p-1.5 rounded text-red-400 hover:text-red-300 hover:bg-red-950/40" title="Eliminar"><Trash2 className="size-4" /></button>
        </div>
      </td>
    </tr>
  )
}

function TypeDialog({ mode, type, onClose }: { mode: 'create' | 'edit'; type?: ProductType; onClose: () => void }) {
  const qc = useQueryClient()
  const [name, setName] = useState(type?.name ?? '')
  const [slug, setSlug] = useState(type?.slug ?? '')

  const save = useMutation({
    mutationFn: (payload: any) =>
      mode === 'create'
        ? tenantApi.post('/api/v1/product-types', payload)
        : tenantApi.patch(`/api/v1/product-types/${type!.id}`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['product-types'] })
      onClose()
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    save.mutate({ name, slug })
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-neutral-800">
          <h2 className="text-lg font-semibold">{mode === 'create' ? 'Nuevo tipo' : 'Editar tipo'}</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-white"><X className="size-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <Label>Nombre</Label>
            <Input
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                if (mode === 'create' && !slug) {
                  setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''))
                }
              }}
              placeholder="Camisetas"
              required
              autoFocus
            />
          </div>
          <div>
            <Label>Slug</Label>
            <Input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))} required />
          </div>
          {save.error && (
            <div className="p-3 rounded bg-red-900/20 border border-red-800 text-sm text-red-400">{(save.error as Error).message}</div>
          )}
          <div className="flex gap-2 justify-end pt-2 border-t border-neutral-800">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={save.isPending}>{save.isPending ? 'Guardando...' : (mode === 'create' ? 'Crear' : 'Guardar')}</Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
